import { atom, useAtom } from "jotai";
import { atomFamily } from "jotai/utils";
import { useEffect, useState } from "react";
import { MondayListItem } from "./monday-list-item";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import { IntegrationType } from "shared/integrations/integration";
import style from "./subitems-task-list.module.css";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { MondayListItemV2 } from "./monday-list-item-v2";

const subitemsAtom = atomFamily((_parentItemId: string) => atom<any[]>([]));
const subitemsIntegrationIdAtom = atomFamily((_parentItemId: string) => atom<string | undefined>(undefined));

export default function SubitemsTaskList({
  documentId,
  parentBoardId,
  subitemsBoard,
  item,
  onBack,
  onCheckItem,
  checkedItems,
}: {
  documentId: string;
  parentBoardId: string;
  subitemsBoard: { id: string; columns: any[] };
  item: any;
  onBack: () => void;
  onCheckItem: (itemId: string) => void;
  checkedItems: string[];
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [subitems, setSubitems] = useAtom(subitemsAtom(item.id));
  const [integrationId, setIntegrationId] = useAtom(subitemsIntegrationIdAtom(item.id));
  const { subitems_ids } = item;

  const newMondayTaskEnabled = useFeatureFlag("new-monday-task-element");

  const { mondayIntegration, addMondayIntegration, fetchItems, getBoardIntegrationConfig } = useBoardTasks(documentId);

  useEffect(() => {
    if (subitems_ids && subitems_ids.length !== subitems.length) {
      fetchSubitems(subitems_ids);
    }
  }, [subitems.length]);

  async function fetchSubitems(subitems_ids: string[]) {
    if (!mondayIntegration) return;

    setIsLoading(true);
    try {
      const id = await addMondayIntegration(
        subitemsBoard.id,
        mondayIntegration.id,
        subitemsBoard.columns,
        undefined,
        undefined,
        parentBoardId,
        true
      );
      setIntegrationId(id);
      const itemsMap = await fetchItems({ [id]: { itemIds: subitems_ids } });
      if (itemsMap && itemsMap[id]) {
        setSubitems(itemsMap[id].items);
      }
    } catch (e) {
      console.error("failed fetching subitems", e);
      // fallback to all items
      onBack();
    } finally {
      setIsLoading(false);
    }
  }

  function renderLoading() {
    const itemsText = subitems_ids.length === 1 ? "subitem" : "subitems";
    return (
      <div className={style.loading}>
        Loading {subitems_ids.length} {itemsText}...
      </div>
    );
  }

  function renderContent() {
    if (!integrationId) {
      return;
    }
    const configuration = getBoardIntegrationConfig(integrationId);
    if (!configuration) {
      return;
    }
    return subitems.map((subitem: any) => {
      const props = {
        item: subitem,
        integrationId,
        integrationType: IntegrationType.monday,
        configuration,
        isChecked: checkedItems.includes(subitem.id),
        onClick: () => onCheckItem(subitem.id),
        showCheckbox: checkedItems.length > 0,
      };

      if (newMondayTaskEnabled) {
        return <MondayListItemV2 key={subitem.id} {...props} />;
      }

      return <MondayListItem key={subitem.id} {...props} />;
    });
  }

  return (
    <>
      <div className={style.header}>
        <div onClick={onBack} className={style.back}>
          ← Back
        </div>
        <div>
          Subitems of <span className={style.itemName}>{item.name}</span>
        </div>
      </div>
      <div className={style.subitemsContainer}>{isLoading ? renderLoading() : renderContent()}</div>
    </>
  );
}
